import type { Dispatch, SetStateAction } from 'react';
import type { TokenData, TokenLifetimeSnapshot } from '@/types/auth';
import Cookies, { CookieAttributes } from 'js-cookie';
import {
  reportTokenRequestDiagnostic,
  type TokenDiagnosticOutcome
} from '@/adapters/errorReporterAdapter';
import { recoverTerminalChatAuthFailure, waitForAuthInitialization } from './authCoordination';
import { isHardChatAuthFailure } from './chatError';
import type { TokenRefreshAttemptResult } from './tabCoordination';
import { createTerminalRefreshError, normalizeTokenRefreshAttemptResult } from './refreshResult';
import { getCanonicalAuthUserIdFromToken } from './userIdentification';
import {
  ACCESS_TOKEN_EXPIRY_BUFFER_MS as TOKEN_EXPIRY_BUFFER_MS,
  decodeAndValidateAccessToken,
  getMonotonicElapsedMs,
  getTokenRequestDiagnostics,
  markTokenRequestDiagnosticReported,
  type TokenRequestOperation,
  wasTokenRequestDiagnosticReported,
  validateTokenResponse
} from './tokenResponseDiagnostics';

export { getCanonicalAuthUserIdFromToken } from './userIdentification';
export type { TokenLifetimeSnapshot } from '@/types/auth';

const ACCESS_TOKEN_COOKIE = 'digitChatAuthToken';
const AUTH_BOOTSTRAP_META_SELECTOR = 'meta[name="digit-chat-auth-bootstrap"]';
export const DIGIT_CHAT_HAS_REFRESH_TOKEN = 'digitChatHasRefreshToken';
export const ACCESS_TOKEN_EXPIRY_BUFFER_MS = TOKEN_EXPIRY_BUFFER_MS;

interface AccessTokenReceipt {
  accessToken: string;
  receivedAtLocalMonotonicMs: number;
  receivedAtLocalWallClockMs: number;
  remainingLifetimeAtReceiptMs: number;
}

interface TokenClockReading {
  localMonotonicMs: number;
  sharedMonotonicMs: number;
}

interface GetValidTokenOptions {
  rejectedToken?: string;
  expectedUserId?: string | null;
}

interface DecodedTokenPayload {
  exp?: number;
  iat?: number;
  userId?: unknown;
  userIdRaw?: unknown;
}

let accessTokenReceipt: AccessTokenReceipt | null = null;
let refreshTokenMarkerWasObserved = false;

export const getHasRefreshToken = (): boolean => {
  try {
    return Cookies.get(DIGIT_CHAT_HAS_REFRESH_TOKEN) === 'true';
  } catch (_error) {
    return false;
  }
};

const getTokenClockReading = (): TokenClockReading => {
  const localMonotonicMs = performance.now();

  return {
    localMonotonicMs,
    sharedMonotonicMs: performance.timeOrigin + localMonotonicMs
  };
};

const normalizeTokenUserId = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return null;
};

const decodeTokenData = (token: string) => {
  const decoded = decodeAndValidateAccessToken(token) as DecodedTokenPayload & {
    exp: number;
    iat: number;
  };

  const lifetimeMs = (decoded.exp - decoded.iat) * 1000;
  const expiresAt = new Date(decoded.exp * 1000);
  const issuedAt = new Date(decoded.iat * 1000);

  if (
    !Number.isFinite(lifetimeMs) ||
    Number.isNaN(expiresAt.getTime()) ||
    Number.isNaN(issuedAt.getTime())
  ) {
    const error = new Error('Invalid access token lifetime claims');
    error.name = 'InvalidAccessTokenClaimsError';
    throw error;
  }

  return {
    lifetimeMs,
    tokenData: {
      expiresAt,
      issuedAt,
      timeRemaining: 0,
      userId: normalizeTokenUserId(decoded.userId),
      userIdRaw: normalizeTokenUserId(decoded.userIdRaw)
    }
  };
};

export const getRemainingAccessTokenLifetimeMs = (
  token: string,
  clockReading = getTokenClockReading()
): number | null => {
  if (!accessTokenReceipt || accessTokenReceipt.accessToken !== token) {
    return null;
  }

  const elapsedMs = getMonotonicElapsedMs(
    accessTokenReceipt.receivedAtLocalMonotonicMs,
    clockReading.localMonotonicMs
  );

  return Math.max(0, accessTokenReceipt.remainingLifetimeAtReceiptMs - elapsedMs);
};

export const accountForAccessTokenSuspension = (
  tokenBeforeSuspension: string | null,
  suspensionGapMs: number
): boolean => {
  if (!accessTokenReceipt || !Number.isFinite(suspensionGapMs) || suspensionGapMs <= 0) {
    return false;
  }

  // A replacement received while hidden only experienced the gap since its own receipt.
  const receiptSuspensionGapMs = Math.max(
    0,
    Date.now() -
      accessTokenReceipt.receivedAtLocalWallClockMs -
      getMonotonicElapsedMs(accessTokenReceipt.receivedAtLocalMonotonicMs)
  );
  const uncountedElapsedMs =
    accessTokenReceipt.accessToken === tokenBeforeSuspension
      ? suspensionGapMs
      : Math.min(suspensionGapMs, receiptSuspensionGapMs);

  if (uncountedElapsedMs === 0) {
    return false;
  }

  accessTokenReceipt.remainingLifetimeAtReceiptMs = Math.max(
    0,
    accessTokenReceipt.remainingLifetimeAtReceiptMs - uncountedElapsedMs
  );
  return true;
};

export const getAccessTokenLifetimeSnapshot = (
  accessToken: string
): TokenLifetimeSnapshot | null => {
  const clockReading = getTokenClockReading();
  const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(accessToken, clockReading);

  if (remainingLifetimeMs === null) {
    return null;
  }

  return {
    measuredAtSharedMonotonicMs: clockReading.sharedMonotonicMs,
    remainingLifetimeMs
  };
};

export const getAccessTokenFromMemory = (): string | null => {
  if (!accessTokenReceipt) {
    return null;
  }

  if (refreshTokenMarkerWasObserved && !getHasRefreshToken()) {
    accessTokenReceipt = null;
    return null;
  }

  const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(accessTokenReceipt.accessToken);

  return remainingLifetimeMs !== null && remainingLifetimeMs > ACCESS_TOKEN_EXPIRY_BUFFER_MS
    ? accessTokenReceipt.accessToken
    : null;
};

function anchorAccessTokenLifetime(
  accessToken: string,
  clockReading = getTokenClockReading(),
  lifetimeSnapshot?: TokenLifetimeSnapshot
): TokenData {
  const { lifetimeMs, tokenData } = decodeTokenData(accessToken);
  const snapshotElapsedMs = lifetimeSnapshot
    ? getMonotonicElapsedMs(
        lifetimeSnapshot.measuredAtSharedMonotonicMs,
        clockReading.sharedMonotonicMs
      )
    : 0;
  const snapshotRemainingLifetimeMs = lifetimeSnapshot
    ? lifetimeSnapshot.remainingLifetimeMs - snapshotElapsedMs
    : lifetimeMs;
  const remainingLifetimeAtReceiptMs = Math.min(lifetimeMs, snapshotRemainingLifetimeMs);

  if (
    !Number.isFinite(remainingLifetimeAtReceiptMs) ||
    remainingLifetimeAtReceiptMs <= ACCESS_TOKEN_EXPIRY_BUFFER_MS ||
    !Number.isFinite(clockReading.localMonotonicMs) ||
    !Number.isFinite(clockReading.sharedMonotonicMs)
  ) {
    const error = new Error('Access token has no usable lifetime');
    error.name = 'UnusableAccessTokenError';
    throw error;
  }

  const refreshTokenMarkerPresent = getHasRefreshToken();

  if (refreshTokenMarkerWasObserved && !refreshTokenMarkerPresent) {
    accessTokenReceipt = null;
    const error = new Error('Chat refresh state disappeared before token application');
    error.name = 'UnusableAccessTokenError';
    throw error;
  }

  refreshTokenMarkerWasObserved ||= refreshTokenMarkerPresent;

  if (accessTokenReceipt?.accessToken !== accessToken) {
    accessTokenReceipt = {
      accessToken,
      receivedAtLocalMonotonicMs: clockReading.localMonotonicMs,
      receivedAtLocalWallClockMs: Date.now(),
      remainingLifetimeAtReceiptMs
    };
  }

  const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(accessToken, clockReading);

  if (remainingLifetimeMs === null || remainingLifetimeMs <= ACCESS_TOKEN_EXPIRY_BUFFER_MS) {
    const error = new Error('Access token has no usable lifetime');
    error.name = 'UnusableAccessTokenError';
    throw error;
  }

  return {
    ...tokenData,
    timeRemaining: Math.floor(remainingLifetimeMs / 1000)
  };
}

export const getAccessTokenFromCookieForBootstrap = (
  expectedUserId?: string | null,
  clockReading = getTokenClockReading()
): string | null => {
  let accessToken: string | null;

  try {
    accessToken = (Cookies.get(ACCESS_TOKEN_COOKIE) as string) || null;
  } catch (_error) {
    return null;
  }

  const bootstrapMeta = document.querySelector<HTMLMetaElement>(AUTH_BOOTSTRAP_META_SELECTOR);
  const serverTimeMs = Number(bootstrapMeta?.dataset.serverTimeMs);
  const renderedUserId = bootstrapMeta?.dataset.userId || null;

  if (!accessToken || !Number.isFinite(serverTimeMs) || !renderedUserId) {
    return null;
  }

  try {
    const { tokenData } = decodeTokenData(accessToken);
    const tokenUserId = tokenData.userIdRaw || tokenData.userId;

    if (
      !tokenUserId ||
      tokenUserId !== renderedUserId ||
      (expectedUserId && tokenUserId !== expectedUserId)
    ) {
      return null;
    }

    const remainingLifetimeMs =
      tokenData.expiresAt.getTime() - serverTimeMs - clockReading.localMonotonicMs;

    anchorAccessTokenLifetime(accessToken, clockReading, {
      measuredAtSharedMonotonicMs: clockReading.sharedMonotonicMs,
      remainingLifetimeMs
    });

    return accessToken;
  } catch (_error) {
    return null;
  }
};

export const clearAccessTokenFromMemory = () => {
  accessTokenReceipt = null;
};

export const resetAccessTokenStateForTests = () => {
  accessTokenReceipt = null;
  refreshTokenMarkerWasObserved = false;
};

export const cookiesOptions: CookieAttributes = {
  secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
  sameSite: 'Lax' as const,
  path: '/',
  domain:
    process.env.COOKIES_DOMAIN ||
    (typeof window !== 'undefined' ? window.location.hostname : undefined)
};

export const extractTokenData = (token: string): TokenData => {
  const { tokenData } = decodeTokenData(token);
  const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(token);

  return {
    ...tokenData,
    timeRemaining: remainingLifetimeMs === null ? 0 : Math.floor(remainingLifetimeMs / 1000)
  };
};

export const getCanonicalAuthUserId = (
  currentUserId?: string | null,
  accessToken?: string | null
): string | null => getCanonicalAuthUserIdFromToken(accessToken) || currentUserId || null;

export const setTokenCookies = (
  accessToken: string,
  options: {
    clockReading?: TokenClockReading;
    lifetimeSnapshot?: TokenLifetimeSnapshot;
  } = {}
): TokenData => {
  const clockReading = options.clockReading || getTokenClockReading();
  const accessTokenData = anchorAccessTokenLifetime(
    accessToken,
    clockReading,
    options.lifetimeSnapshot
  );
  const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(accessToken, clockReading);

  if (remainingLifetimeMs === null) {
    const error = new Error('Access token has no receipt anchor');
    error.name = 'UnusableAccessTokenError';
    throw error;
  }

  const cookieMaxAgeSeconds = Math.max(
    1,
    Math.floor((remainingLifetimeMs - ACCESS_TOKEN_EXPIRY_BUFFER_MS) / 1000)
  );

  try {
    Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, {
      'max-age': String(cookieMaxAgeSeconds),
      ...cookiesOptions
    });
  } catch (_error) {
    // The in-memory receipt remains authoritative when cookies are unavailable.
  }

  return accessTokenData;
};

export const clearTokenCookies = () => {
  clearAccessTokenFromMemory();
  Cookies.remove(ACCESS_TOKEN_COOKIE, cookiesOptions);
};

export const clearRefreshTokenMarker = () => {
  Cookies.remove(DIGIT_CHAT_HAS_REFRESH_TOKEN, cookiesOptions);
};

export const getTokensFromCookies = (): string | null => {
  let accessToken = '';

  try {
    accessToken = (Cookies.get(ACCESS_TOKEN_COOKIE) as string) || '';
  } catch (_error) {
    return null;
  }

  if (!accessToken) {
    return null;
  }

  return accessToken;
};

export const handleAccessToken = (
  accessToken: string,
  setTokenExpiresAt: Dispatch<SetStateAction<Date | null>>,
  setHasAccessToken: Dispatch<SetStateAction<boolean>>,
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>,
  lifetimeSnapshot?: TokenLifetimeSnapshot
): TokenData | null => {
  try {
    const tokenData = setTokenCookies(accessToken, { lifetimeSnapshot });

    if (tokenData.expiresAt) {
      setTokenExpiresAt(tokenData.expiresAt);
    }
    setHasAccessToken(true);
    setIsAuthenticated(true);
    return tokenData;
  } catch (_error) {
    return null;
  }
};

export const isTokenExpiredOrExpiring = (token: string): boolean => {
  try {
    decodeTokenData(token);
    const remainingLifetimeMs = getRemainingAccessTokenLifetimeMs(token);
    return remainingLifetimeMs === null || remainingLifetimeMs <= ACCESS_TOKEN_EXPIRY_BUFFER_MS;
  } catch (error: unknown) {
    return true;
  }
};

export const isAuthFailureError = (error: unknown): boolean => isHardChatAuthFailure(error);

const ERROR_VALUE_MAX_LENGTH = 500;
const ERROR_VALUE_TRUNCATION_MARKER = '…[truncated]';
const TOKEN_LIKE_VALUE_PATTERN = /eyJ[\w-]{8,}(?:\.[\w-]{8,}){0,2}/g;

const truncateErrorValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }

  const redactedValue = value.replace(TOKEN_LIKE_VALUE_PATTERN, '[redacted-token]');

  if (redactedValue.length <= ERROR_VALUE_MAX_LENGTH) {
    return redactedValue;
  }

  return `${redactedValue.slice(0, ERROR_VALUE_MAX_LENGTH)}${ERROR_VALUE_TRUNCATION_MARKER}`;
};

const extractResponseErrorField = (
  data: unknown,
  fieldName: 'code' | 'error' | 'message'
): string | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  return truncateErrorValue((data as Record<string, unknown>)[fieldName]);
};

export function getValidatedAccessToken(tokenResponse: unknown): string {
  return validateTokenResponse(tokenResponse, {
    operation: 'bootstrap'
  }).accessToken;
}

export function reportTokenRequestFailure(
  error: unknown,
  operation: TokenRequestOperation,
  outcome: TokenDiagnosticOutcome
): void {
  const tokenRequest = getTokenRequestDiagnostics(error);

  if (
    !tokenRequest ||
    tokenRequest.operation !== operation ||
    wasTokenRequestDiagnosticReported(error)
  ) {
    return;
  }

  markTokenRequestDiagnosticReported(error);
  (error as Error & { chatWebHandled: boolean }).chatWebHandled = true;
  reportTokenRequestDiagnostic(tokenRequest, outcome);
}

export const getValidToken = async (
  waitForTokenRefresh: () => Promise<void>,
  requestTokenRefresh?: (
    expectedUserId?: string | null
  ) => Promise<boolean | TokenRefreshAttemptResult>,
  options: GetValidTokenOptions = {}
): Promise<string | null> => {
  await waitForTokenRefresh();

  const isTokenForExpectedUser = (token: string): boolean => {
    if (!options.expectedUserId) {
      return true;
    }

    return getCanonicalAuthUserIdFromToken(token) === options.expectedUserId;
  };

  const getUsableToken = (): string | null => {
    const memoryToken = getAccessTokenFromMemory();

    if (
      memoryToken &&
      !isTokenExpiredOrExpiring(memoryToken) &&
      isTokenForExpectedUser(memoryToken)
    ) {
      return memoryToken;
    }

    const token = getTokensFromCookies();

    if (!token || isTokenExpiredOrExpiring(token)) {
      return null;
    }

    if (!isTokenForExpectedUser(token)) {
      return null;
    }

    return token;
  };

  const isAcceptableToken = (token: string | null): token is string =>
    Boolean(token && token !== options.rejectedToken);

  const usableToken = getUsableToken();

  if (isAcceptableToken(usableToken)) {
    return usableToken;
  }

  const recoverTokenAfterTerminalFailure = async (error: unknown): Promise<string | null> => {
    const recovered = await recoverTerminalChatAuthFailure(error);

    if (!recovered) {
      return null;
    }

    await waitForAuthInitialization();
    const recoveredToken = getUsableToken();
    return isAcceptableToken(recoveredToken) ? recoveredToken : null;
  };

  if (!getHasRefreshToken()) {
    return recoverTokenAfterTerminalFailure(new Error('Chat refresh state is unavailable'));
  }

  if (!requestTokenRefresh) {
    return null;
  }

  const refreshResult = normalizeTokenRefreshAttemptResult(
    await requestTokenRefresh(options.expectedUserId)
  );

  if (!refreshResult.success) {
    const refreshedTokenAfterFailure = getUsableToken();

    if (isAcceptableToken(refreshedTokenAfterFailure)) {
      return refreshedTokenAfterFailure;
    }

    if (refreshResult.terminalFailure) {
      return recoverTokenAfterTerminalFailure(createTerminalRefreshError(refreshResult));
    }

    if (!getHasRefreshToken()) {
      return recoverTokenAfterTerminalFailure(
        new Error('Chat refresh failed after refresh state was lost')
      );
    }

    return null;
  }

  await waitForTokenRefresh();

  const refreshedToken = getUsableToken();

  if (isAcceptableToken(refreshedToken)) {
    return refreshedToken;
  }

  if (!getHasRefreshToken()) {
    return recoverTokenAfterTerminalFailure(
      new Error('Chat refresh completed without leaving a usable access token')
    );
  }

  return null;
};

export const isRequestCancellationError = (error: unknown): boolean => {
  const requestError = error as { name?: string; code?: string } | null;
  const tokenRequest = getTokenRequestDiagnostics(error);

  return (
    tokenRequest?.transport === 'cancelled' ||
    requestError?.name === 'AbortError' ||
    requestError?.name === 'CanceledError' ||
    requestError?.code === 'ERR_CANCELED'
  );
};

const normalizeRequestPath = (url: unknown): string | undefined => {
  if (typeof url !== 'string' || !url.trim()) {
    return undefined;
  }

  try {
    return new URL(url, window.location.origin).pathname;
  } catch (_error) {
    return url.split('?')[0];
  }
};

export const getSafeRequestErrorMetadata = (error: unknown): Record<string, unknown> => {
  const requestError = error as {
    code?: string;
    config?: {
      method?: string;
      url?: string;
    };
    response?: {
      status?: number;
      data?: unknown;
    };
  } | null;
  const requestPath = normalizeRequestPath(requestError?.config?.url);
  const responseData = requestError?.response?.data;
  const responseErrorCode = extractResponseErrorField(responseData, 'code');
  const responseErrorMessage = extractResponseErrorField(responseData, 'error');
  const responseMessage = extractResponseErrorField(responseData, 'message');

  return {
    ...(requestError?.code && { axiosCode: requestError.code }),
    ...(requestError?.response?.status && { status: requestError.response.status }),
    ...(requestError?.config?.method && {
      requestMethod: requestError.config.method.toUpperCase()
    }),
    ...(requestPath && { requestPath }),
    ...(responseErrorCode && { responseErrorCode }),
    ...(responseErrorMessage && { responseErrorMessage }),
    ...(responseMessage && { responseMessage }),
    ...(typeof navigator !== 'undefined' && { online: navigator.onLine }),
    ...(typeof document !== 'undefined' && { visibilityState: document.visibilityState })
  };
};
